import { getLocalToday } from "utils/Common/Date";
import DateSelect from "Components/Common/DateSelect/DateSelect";
import Input from "Components/Common/Input/Input";
import {
  InspectorForm,
  Metrics,
  ResidentialPhase,
} from "../types/inspector.types";
import Button from "Components/Common/Button/Button";

interface ResidentialSubmitTabProps {
  phases: ResidentialPhase[];
  metrics: Metrics;
  gcBilledPct: string;
  inspectorForm: InspectorForm;
  generalNotes: string;
  drawNarrative: string;
  scheduleNotes: string;
  bankNotes: string;
  submitted: boolean;
  projectName: string;
  isSaving: boolean;
  isSubmitting: boolean;
  saveError: string;
  submitError: string;
  saveDraftSuccess: boolean;
  onInspectorFormChange: (field: keyof InspectorForm, value: string) => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
  onNewInspection: () => void;
}

export default function ResidentialSubmitTab({
  phases,
  metrics,
  gcBilledPct,
  inspectorForm,
  generalNotes,
  drawNarrative,
  scheduleNotes,
  bankNotes,
  submitted,
  projectName,
  isSaving,
  isSubmitting,
  saveError,
  submitError,
  saveDraftSuccess,
  onInspectorFormChange,
  onSubmit,
  onSaveDraft,
  onNewInspection,
}: ResidentialSubmitTabProps) {
  const busy = isSaving || isSubmitting;
  const today = getLocalToday();
  const totalItems = phases.reduce((sum, p) => sum + p.items.length, 0);
  const fiComplete = metrics.inspectedCount >= totalItems && totalItems > 0;
  const photosComplete = metrics.photosCount > 0;
  const narrativeComplete = !!(
    drawNarrative ||
    scheduleNotes ||
    bankNotes ||
    generalNotes
  );

  if (submitted) {
    return (
      <div className="successCard">
        <div className="successScreen">
          <div className="successCircle">✓</div>
          <div className="successTitle">Report Submitted</div>
          <div className="successSub">
            Inspection #{inspectorForm.number} for{" "}
            {projectName || "this project"} has been submitted to CSC New
            England. {metrics.flagsCount} flag
            {metrics.flagsCount !== 1 ? "s" : ""} noted — the progress report
            will be prepared and sent to {inspectorForm.bank}.
          </div>
          <Button className="newInspectionBtn" onClick={onNewInspection}>
            Start New Inspection
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="submitChecklist">
        <div className="scItem">
          <div className={`scIcon ${fiComplete ? "done" : "pending"}`}>
            {fiComplete ? "✓" : "!"}
          </div>
          <div className="scText">
            <div className="scLabel">FI Form — all phases reviewed</div>
            <div className="scSub">
              {phases.length} phases · {totalItems} line items ·{" "}
              {metrics.flagsCount} flag{metrics.flagsCount !== 1 ? "s" : ""}{" "}
              raised
            </div>
          </div>
        </div>

        <div className="scItem">
          <div className={`scIcon ${photosComplete ? "done" : "pending"}`}>
            {photosComplete ? "✓" : "!"}
          </div>
          <div className="scText">
            <div className="scLabel">Photos captured</div>
            <div className="scSub">
              {metrics.photosCount > 0
                ? `${metrics.photosCount} photo${metrics.photosCount !== 1 ? "s" : ""} with captions`
                : "Add photos before submitting"}
            </div>
          </div>
        </div>

        <div className="scItem">
          <div className={`scIcon ${narrativeComplete ? "done" : "pending"}`}>
            {narrativeComplete ? "✓" : "!"}
          </div>
          <div className="scText">
            <div className="scLabel">Narrative notes complete</div>
            <div className="scSub">
              Draw review, flags, schedule, bank items
            </div>
          </div>
        </div>

        <div className="scItem">
          <div className={"scIcon pending"}>!</div>
          <div className="scText">
            <div className="scLabel">Inspector signature required</div>
            <div className="scSub">
              Fill in inspector details below and submit
            </div>
          </div>
        </div>
      </div>

      <div className="rSummarySection">
        <div className="rSectionTitle">INSPECTION SUMMARY</div>
        <div className="rSummaryGrid">
          <div className="rSummaryCell">
            <div className="rSummaryCellLabel">CSC ESTIMATE</div>
            <div className="rSummaryCellValue">{metrics.avgPct}%</div>
          </div>
          <div className="rSummaryCell">
            <div className="rSummaryCellLabel">GC BILLED</div>
            <div className="rSummaryCellValue amber">{gcBilledPct || "—"}</div>
          </div>
          <div className="rSummaryCell">
            <div className="rSummaryCellLabel">FLAGS</div>
            <div
              className={`rSummaryCellValue${metrics.flagsCount > 0 ? " red" : ""}`}
            >
              {metrics.flagsCount}
            </div>
          </div>
          <div className="rSummaryCell">
            <div className="rSummaryCellLabel">PHOTOS</div>
            <div
              className={`rSummaryCellValue${metrics.photosCount > 0 ? " green" : ""}`}
            >
              {metrics.photosCount}
            </div>
          </div>
        </div>
      </div>

      <div className="rDetailsSection">
        <div className="rSectionTitle">INSPECTOR DETAILS</div>
        {[
          {
            field: "name" as keyof InspectorForm,
            label: "INSPECTOR NAME",
            type: "text",
            readOnly: false,
          },
          {
            field: "number" as keyof InspectorForm,
            label: "INSPECTION #",
            type: "text",
            readOnly: true,
          },
          {
            field: "date" as keyof InspectorForm,
            label: "INSPECTION DATE",
            type: "date",
            readOnly: true,
          },
          {
            field: "bank" as keyof InspectorForm,
            label: "BANK",
            type: "text",
            readOnly: true,
          },
        ].map(({ field, label, type, readOnly }) => (
          <div className="inspectorField" key={field}>
            <div className="inspectorLabel">{label}</div>
            {type === "date" ? (
              <DateSelect
                value={today}
                onChange={() => {}}
                disabled={readOnly || busy}
                className="inspectionDate"
              />
            ) : (
              <Input
                className={`inspectorInput${readOnly ? " readOnly" : ""}`}
                type={type}
                value={inspectorForm[field]}
                onChange={(e) =>
                  !readOnly && onInspectorFormChange(field, e.target.value)
                }
                readOnly={readOnly}
                disabled={!readOnly && busy}
                aria-label={label}
              />
            )}
          </div>
        ))}
      </div>

      {submitError && <div className="submitMsg error">{submitError}</div>}
      {saveError && <div className="submitMsg error">{saveError}</div>}
      {saveDraftSuccess && (
        <div className="submitMsg success">Draft saved successfully.</div>
      )}

      <Button className="submitReportBtn" onClick={onSubmit} disabled={busy}>
        {isSubmitting ? "Submitting…" : "✓  Submit Inspection Report"}
      </Button>
      <Button className="saveDraftBtn" onClick={onSaveDraft} disabled={busy}>
        {isSaving ? "Saving…" : "Save Draft & Continue Later"}
      </Button>
    </>
  );
}
